import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { makeStyles, theme, LayoutGrid, Typography, Datepicker, Timepicker, Button } from '@archer-technologies/ui-components';
import { useRef, useReducer, useEffect, createElement, Fragment } from 'react';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import moment from 'moment';
var _excluded = ["lastModifiedField"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var useStyles = makeStyles(function () {
  return {
    filterContainer: {
      minWidth: 'auto'
    },
    filterLabel: {
      display: 'flex',
      alignItems: 'center'
    },
    filterWrapper: {
      padding: theme.spacing(0, 1, 0, 1)
    }
  };
});

var extractFilterValues = function extractFilterValues(filter) {
  var filterState = {
    startDate: null,
    startTime: null,
    endDate: null,
    endTime: null
  };

  if (filter && filter.value) {
    var startDateTime = moment(filter.value.startDateTime);
    var endDateTime = moment(filter.value.endDateTime);
    filterState = {
      startDate: moment({
        year: startDateTime.get('year'),
        month: startDateTime.get('month'),
        date: startDateTime.get('date')
      }),
      startTime: moment({
        hour: startDateTime.get('hour'),
        minute: startDateTime.get('minute')
      }),
      endDate: moment({
        year: endDateTime.get('year'),
        month: endDateTime.get('month'),
        date: endDateTime.get('date')
      }),
      endTime: moment({
        hour: endDateTime.get('hour'),
        minute: endDateTime.get('minute')
      })
    };
  }

  return filterState;
};

var combineDateTime = function combineDateTime(date, time) {
  return new Date(date.get('year'), date.get('month'), date.get('date'), time.get('hour'), time.get('minute'));
};

var combineFilterValues = function combineFilterValues(filterState) {
  var startDate = filterState.startDate,
      startTime = filterState.startTime,
      endDate = filterState.endDate,
      endTime = filterState.endTime;

  if (startDate && startTime && endDate && endTime) {
    return {
      startDateTime: combineDateTime(startDate, startTime),
      endDateTime: combineDateTime(endDate, endTime)
    };
  } else if (startDate && startTime) {
    return {
      startDateTime: combineDateTime(startDate, startTime),
      endDateTime: moment()
    };
  } else if (endDate && endTime) {
    return {
      startDateTime: moment(0),
      endDateTime: combineDateTime(endDate, endTime)
    };
  }

  return null;
};

var isFilterInputEmpty = function isFilterInputEmpty(date) {
  return date === null;
};

var checkClearButtonDisabledState = function checkClearButtonDisabledState(_ref) {
  var startDate = _ref.startDate,
      startTime = _ref.startTime,
      endDate = _ref.endDate,
      endTime = _ref.endTime;
  return Object.values({
    startDate: startDate,
    startTime: startTime,
    endDate: endDate,
    endTime: endTime
  }).every(isFilterInputEmpty);
};

var checkApplyButtonDisabledState = function checkApplyButtonDisabledState(filter, _ref2, invalidDateTime) {
  var startDate = _ref2.startDate,
      startTime = _ref2.startTime,
      endDate = _ref2.endDate,
      endTime = _ref2.endTime;
  var isDisabled = invalidDateTime || Object.values({
    startDate: startDate,
    startTime: startTime
  }).some(isFilterInputEmpty) || Object.values({
    endDate: endDate,
    endTime: endTime
  }).some(isFilterInputEmpty);
  var combinedFilterValues = combineFilterValues({
    startDate: startDate,
    startTime: startTime,
    endDate: endDate,
    endTime: endTime
  });

  if (!isDisabled && filter && combinedFilterValues) {
    var startDateTime = combinedFilterValues.startDateTime,
        endDateTime = combinedFilterValues.endDateTime;
    isDisabled = moment(startDateTime).isSame(filter.value.startDateTime) && moment(endDateTime).isSame(filter.value.endDateTime);
  }

  return isDisabled;
};

var initFilterState = function initFilterState(filter) {
  return extractFilterValues(filter);
}; // @ts-ignore


var filterStateReducer = function filterStateReducer(state, action) {
  var defaultStartTime = moment({
    hour: 0,
    minute: 0
  });
  var defaultEndTime = moment({
    hour: 23,
    minute: 59
  });
  var currentDate = moment({
    hour: 0,
    minute: 0
  });

  switch (action.type) {
    case 'startDate':
      var startDateUpdate = state.startTime ? _objectSpread(_objectSpread({}, state), {}, {
        startDate: action.date,
        lastModifiedField: action.type
      }) : _objectSpread(_objectSpread({}, state), {}, {
        startDate: action.date,
        startTime: defaultStartTime,
        lastModifiedField: action.type
      });
      return startDateUpdate;

    case 'startTime':
      var startTimeUpdate = state.startDate ? _objectSpread(_objectSpread({}, state), {}, {
        startTime: action.date,
        lastModifiedField: action.type
      }) : _objectSpread(_objectSpread({}, state), {}, {
        startTime: action.date,
        startDate: currentDate,
        lastModifiedField: action.type
      });
      return startTimeUpdate;

    case 'endDate':
      var endDateUpdate = state.endTime ? _objectSpread(_objectSpread({}, state), {}, {
        endDate: action.date,
        lastModifiedField: action.type
      }) : _objectSpread(_objectSpread({}, state), {}, {
        endDate: action.date,
        endTime: defaultEndTime,
        lastModifiedField: action.type
      });
      return endDateUpdate;

    case 'endTime':
      var endTimeUpdate = state.endDate ? _objectSpread(_objectSpread({}, state), {}, {
        endTime: action.date,
        lastModifiedField: action.type
      }) : _objectSpread(_objectSpread({}, state), {}, {
        endTime: action.date,
        endDate: currentDate,
        lastModifiedField: action.type
      });
      return endTimeUpdate;

    case 'clear':
    default:
      return initFilterState(null);
  }
};

var dateColumnIntegratedFilterPredicate = function dateColumnIntegratedFilterPredicate(value, filter) {
  var _filter$value = filter.value,
      startDateTime = _filter$value.startDateTime,
      endDateTime = _filter$value.endDateTime;
  return moment(value).isSameOrAfter(startDateTime) && moment(value).isSameOrBefore(endDateTime);
};

var useDateColumnFilterRefs = function useDateColumnFilterRefs() {
  var startDateFilterRef = useRef(null);
  var startTimeFilterRef = useRef(null);
  var endDateFilterRef = useRef(null);
  var endTimeFilterRef = useRef(null);
  return {
    childComponentRefs: {
      startDateFilterRef: startDateFilterRef,
      startTimeFilterRef: startTimeFilterRef,
      endDateFilterRef: endDateFilterRef,
      endTimeFilterRef: endTimeFilterRef
    }
  };
};

var DateColumnFilter = function DateColumnFilter(_ref3) {
  var filter = _ref3.filter,
      columnName = _ref3.columnName,
      onFilter = _ref3.onFilter,
      addClearFilterStateFn = _ref3.addClearFilterStateFn,
      removeClearFilterStateFn = _ref3.removeClearFilterStateFn,
      messages = _ref3.messages,
      translate = _ref3.translate,
      dateTimeLocaleCode = _ref3.dateTimeLocaleCode,
      _ref3$filterComponent = _ref3.filterComponentRefs,
      filterComponentRefs = _ref3$filterComponent === void 0 ? {} : _ref3$filterComponent;

  var _useStyles = useStyles(),
      filterContainer = _useStyles.filterContainer,
      filterLabel = _useStyles.filterLabel,
      filterWrapper = _useStyles.filterWrapper;

  var _React$useReducer = useReducer(filterStateReducer, filter, initFilterState),
      _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
      filterState = _React$useReducer2[0],
      dispatch = _React$useReducer2[1];

  var _ref4 = filterComponentRefs.childComponentRefs || {},
      startDateFilterRef = _ref4.startDateFilterRef,
      startTimeFilterRef = _ref4.startTimeFilterRef,
      endDateFilterRef = _ref4.endDateFilterRef,
      endTimeFilterRef = _ref4.endTimeFilterRef;

  var dateTimeErrorProps = validateDateTime(filterState, messages);
  var invalidDateTimeRange = Object.values(dateTimeErrorProps).some(function (props) {
    return props.error;
  });

  var startDateProps = _objectSpread({
    fullWidth: true,
    menuRef: startDateFilterRef,
    label: messages.dateFilterLabel,
    locale: dateTimeLocaleCode,
    translate: translate,
    value: filterState.startDate,
    onChange: function onChange(date) {
      return dispatch({
        type: 'startDate',
        date: date
      });
    }
  }, dateTimeErrorProps.startDate);

  var startTimeProps = _objectSpread({
    fullWidth: true,
    menuRef: startTimeFilterRef,
    label: messages.timeFilterLabel,
    locale: dateTimeLocaleCode,
    translate: translate,
    value: filterState.startTime,
    onChange: function onChange(date) {
      return dispatch({
        type: 'startTime',
        date: date
      });
    }
  }, dateTimeErrorProps.startTime);

  var endDateProps = _objectSpread({
    fullWidth: true,
    menuRef: endDateFilterRef,
    label: messages.dateFilterLabel,
    locale: dateTimeLocaleCode,
    translate: translate,
    value: filterState.endDate,
    onChange: function onChange(date) {
      return dispatch({
        type: 'endDate',
        date: date
      });
    }
  }, dateTimeErrorProps.endDate);

  var endTimeProps = _objectSpread({
    fullWidth: true,
    menuRef: endTimeFilterRef,
    label: messages.timeFilterLabel,
    locale: dateTimeLocaleCode,
    translate: translate,
    value: filterState.endTime,
    onChange: function onChange(date) {
      return dispatch({
        type: 'endTime',
        date: date
      });
    }
  }, dateTimeErrorProps.endTime);

  var clearButtonDisabled = checkClearButtonDisabledState(filterState);
  var applyButtonDisabled = checkApplyButtonDisabledState(filter, filterState, invalidDateTimeRange);
  var onFilterClear = clearButtonDisabled ? noop : function () {
    dispatch({
      type: 'clear',
      date: null
    });
    onFilter(null);
    document.body.click();
  };

  var applyfilter = function applyfilter() {
    onFilter({
      value: combineFilterValues(filterState)
    });
    document.body.click();
  };

  var onFilterApply = applyButtonDisabled ? noop : applyfilter;
  useEffect(function () {
    var clearFilterState = function clearFilterState() {
      return dispatch({
        type: 'clear',
        date: null
      });
    };

    addClearFilterStateFn(columnName, clearFilterState);
    return function () {
      return removeClearFilterStateFn(columnName);
    };
  }, [addClearFilterStateFn, removeClearFilterStateFn, columnName, dispatch]);
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(LayoutGrid, {
    container: true,
    justify: "space-between",
    className: filterContainer
  }, /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 2,
    className: filterLabel
  }, /*#__PURE__*/createElement(Typography, {
    variant: "body1",
    color: "textPrimary"
  }, messages.dateTimeFilterStart)), /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 5,
    className: filterWrapper
  }, /*#__PURE__*/createElement(Datepicker, startDateProps)), /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 5,
    className: filterWrapper
  }, /*#__PURE__*/createElement(Timepicker, startTimeProps))), /*#__PURE__*/createElement(LayoutGrid, {
    container: true,
    justify: "space-between",
    className: filterContainer
  }, /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 2,
    className: filterLabel
  }, /*#__PURE__*/createElement(Typography, {
    variant: "body1",
    color: "textPrimary"
  }, messages.dateTimeFilterEnd)), /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 5,
    className: filterWrapper
  }, /*#__PURE__*/createElement(Datepicker, endDateProps)), /*#__PURE__*/createElement(LayoutGrid, {
    item: true,
    xs: 5,
    className: filterWrapper
  }, /*#__PURE__*/createElement(Timepicker, endTimeProps))), /*#__PURE__*/createElement(LayoutGrid, {
    container: true,
    justify: "flex-end"
  }, /*#__PURE__*/createElement(LayoutGrid, {
    item: true
  }, /*#__PURE__*/createElement(Button, {
    color: "primary",
    variant: "text",
    size: "small",
    disabled: clearButtonDisabled,
    onClick: onFilterClear
  }, messages.clear)), /*#__PURE__*/createElement(LayoutGrid, {
    item: true
  }, /*#__PURE__*/createElement(Button, {
    color: "primary",
    size: "small",
    disabled: applyButtonDisabled,
    onClick: onFilterApply
  }, messages.apply))));
};
/**
 * Utilities
 */


function noop() {}

function validateDateTime(_ref5, messages) {
  var lastModifiedField = _ref5.lastModifiedField,
      filterState = _objectWithoutProperties(_ref5, _excluded);

  var errorProps = {
    startDate: {},
    startTime: {},
    endDate: {},
    endTime: {}
  };
  var startDate = filterState.startDate,
      endDate = filterState.endDate; // If both dates (start date and end date) don't exist then return early

  if (startDate === null || endDate === null) return errorProps;
  var combinedFilterValues = combineFilterValues(filterState); // If the dates are exactly the same, test the times instead if both exist

  if (moment(startDate).isSame(endDate) && combinedFilterValues) {
    var startDateTime = combinedFilterValues.startDateTime,
        endDateTime = combinedFilterValues.endDateTime;

    if (lastModifiedField !== 'endTime' && moment(startDateTime).isAfter(endDateTime)) {
      errorProps.startTime = {
        error: true,
        helperText: messages.startTimeAfterEndTimeError
      };
    }

    if (lastModifiedField !== 'startTime' && moment(endDateTime).isBefore(startDateTime)) {
      errorProps.endTime = {
        error: true,
        helperText: messages.endTimeBeforeStartTimeError
      };
    }
  } else {
    if (lastModifiedField !== 'endDate' && moment(startDate).isAfter(endDate)) {
      errorProps.startDate = {
        error: true,
        helperText: messages.startDateAfterEndDateError
      };
    }

    if (lastModifiedField !== 'startDate' && moment(endDate).isBefore(startDate)) {
      errorProps.endDate = {
        error: true,
        helperText: messages.endDateBeforeStartDateError
      };
    }
  }

  return errorProps;
}

export { DateColumnFilter, checkApplyButtonDisabledState, checkClearButtonDisabledState, combineDateTime, combineFilterValues, dateColumnIntegratedFilterPredicate, extractFilterValues, isFilterInputEmpty, useDateColumnFilterRefs };